import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, Smartphone, X } from "lucide-react";
import { isIosSafari, isStandalonePwa, PWA_INSTALL_PROMPT_ENABLED } from "@/lib/pwa";

const DISMISS_KEY = "dsite_pwa_install_dismissed";

export default function PwaInstallPrompt() {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [hidden, setHidden] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");

  const isPublicPage =
    !location.pathname.startsWith("/admin") &&
    !location.pathname.startsWith("/mypage") &&
    !location.pathname.startsWith("/login") &&
    !location.pathname.startsWith("/signup");

  useEffect(() => {
    if (!PWA_INSTALL_PROMPT_ENABLED || hidden || !isPublicPage || isStandalonePwa()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setShowIosHint(false);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    if (isIosSafari() && !isStandalonePwa()) {
      setShowIosHint(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [hidden, isPublicPage]);

  if (!PWA_INSTALL_PROMPT_ENABLED) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setHidden(true);
    setDeferredPrompt(null);
    setShowIosHint(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  if (hidden || !isPublicPage || isStandalonePwa()) return null;
  if (!deferredPrompt && !showIosHint) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-50"
      data-testid="pwa-install-prompt"
    >
      <div className="bg-[#0a0a0a] border border-white/15 rounded-2xl shadow-2xl shadow-black/60 p-4 flex gap-3 items-start">
        <div className="w-10 h-10 rounded-xl bg-[#0055FF]/15 border border-[#0055FF]/30 flex items-center justify-center shrink-0 text-[#0055FF]">
          {showIosHint ? <Smartphone size={18} /> : <Download size={18} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white">DSiteSolution 앱으로 설치</div>
          <p className="text-xs text-white/55 mt-1 leading-relaxed">
            {showIosHint && !deferredPrompt
              ? "Safari 공유 버튼 → 「홈 화면에 추가」로 바로가기를 만들 수 있습니다."
              : "홈 화면에 추가하면 앱처럼 빠르게 접속할 수 있습니다."}
          </p>
          {deferredPrompt && (
            <button
              type="button"
              onClick={install}
              className="mt-3 text-xs bg-white text-black px-3 py-1.5 rounded-full font-medium hover:bg-white/90"
            >
              앱 설치
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="text-white/40 hover:text-white shrink-0 p-1"
          aria-label="닫기"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
